// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Modal, Platform, Pressable, ScrollView, Share, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import QRCode from 'react-native-qrcode-svg';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from './user-context';
import {
  getSharedBffWebSocket,
  TourCreatedEvent,
  TourInitiateRejectedEvent,
} from '@/services/bffWebSocket';
import { setCurrentRoundId } from '@/services/currentRound';
import { useI18n } from '@/i18n';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { ensureCurrentPlayer } from '@/services/ensureCurrentPlayer';
import MyToursScreen from './my-tours.screen';
import type { TourRoundSummary } from '@/features/round-init/types';
import type { GameDefinition } from '@/features/tour-leaderboard/types';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { getAssetImageSource } from '@/services/assetImageSource';
import { resolveTourInviteLink } from '@/services/tourInviteLink';
import appColors from '@/theme/colors';

const MOBILE_BREAKPOINT = 820;

// Order a tour's rounds by playing date (earliest first), falling back to creation time.
const roundPlayDateOrder = (round: { playDate?: string; createdAt?: string }) => {
  const candidate = round.playDate ?? round.createdAt;
  if (!candidate) return 0;
  const parsed = Date.parse(candidate);
  return Number.isFinite(parsed) ? parsed : 0;
};

type TourSummary = {
  id: string;
  tourName?: string;
  status?: string;
  isHidden?: boolean;
  playerNames?: string[];
  createdAt?: string;
  imageKey?: string | null;
  imageThumbKey?: string | null;
};

type TourRoundsState = {
  loading: boolean;
  error: string | null;
  rounds: TourRoundSummary[];
};

type RoundGamesState = {
  loading: boolean;
  error: string | null;
  gameIds: string[];
};

export default function MyToursWebScreen() {
  const { width } = useWindowDimensions();
  const isMobileLayout = width < MOBILE_BREAKPOINT;

  const { debug: debugParam } = useLocalSearchParams<{ debug?: string }>();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user, clear } = useUser();
  const { t } = useI18n();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const trophyImage = useMemo(() => getAssetImageSource('images/trophy.png', 'icon', '/my-tours'), []);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const [tours, setTours] = useState<TourSummary[]>([]);
  const [statusNote, setStatusNote] = useState<string>(t('tournaments.loading'));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [tourStarting, setTourStarting] = useState(false);
  const [openTourMenuId, setOpenTourMenuId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showArchivedSingleRounds, setShowArchivedSingleRounds] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalTourName, setShareModalTourName] = useState<string | null>(null);
  const [shareModalLink, setShareModalLink] = useState<string | null>(null);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareModalError, setShareModalError] = useState<string | null>(null);
  const [roundsByTourId, setRoundsByTourId] = useState<Record<string, TourRoundsState>>({});
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [roundGamesByRoundId, setRoundGamesByRoundId] = useState<Record<string, RoundGamesState>>({});
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const pendingRequestIdRef = useRef<string | null>(null);
  const pendingStatusRequestIdRef = useRef<string | null>(null);
  const pendingTourPayloadRef = useRef<Record<string, unknown> | null>(null);
  const currentPlayerIdRef = useRef<string | null>(null);
  const gameDefinitionsLoadedForUserIdRef = useRef<string | null>(null);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'my-tours',
      }),
    [apiBaseUrl, user?.token]
  );

  const visibleTours = useMemo(() => {
    const nonHiddenTours = tours.filter((tour) => !tour.isHidden);
    if (showArchived) return nonHiddenTours;
    return nonHiddenTours.filter((tour) => (tour.status ?? '').toLowerCase() !== 'archived');
  }, [showArchived, tours]);
  const myToursAndRoundsSummary = useMemo(() => {
    const tourCount = visibleTours.length;
    const unfinishedRoundCount = visibleTours.reduce((total, tour) => {
      const rounds = roundsByTourId[tour.id]?.rounds ?? [];
      const unfinishedRounds = rounds.filter((round) => {
        const status = (round.status ?? '').toLowerCase();
        return status !== 'cancelled' && status !== 'finished';
      });
      return total + unfinishedRounds.length;
    }, 0);
    if (tourCount === 0 && unfinishedRoundCount === 0) return null;
    return t('tournaments.myToursAndRoundsSummary', { tours: tourCount, rounds: unfinishedRoundCount });
  }, [roundsByTourId, t, visibleTours]);
  const singleRoundRows = useMemo(() => {
    const rows: Array<{ tourId: string; tourName: string; round: TourRoundSummary }> = [];
    tours.forEach((tour) => {
      if (!tour.isHidden) {
        return;
      }
      if (!showArchivedSingleRounds && (tour.status ?? '').toLowerCase() === 'archived') {
        return;
      }
      const rounds = roundsByTourId[tour.id]?.rounds ?? [];
      rounds.forEach((round) => {
        const status = (round.status ?? '').toLowerCase();
        if (status === 'cancelled') {
          return;
        }
        if (!showArchivedSingleRounds && status === 'finished') {
          return;
        }
        rows.push({
          tourId: tour.id,
          tourName: tour.tourName ?? t('tournaments.defaultName'),
          round,
        });
      });
    });
    return rows.sort((a, b) => {
      const aTime = Date.parse(a.round.statusUpdatedAt ?? a.round.createdAt ?? '') || 0;
      const bTime = Date.parse(b.round.statusUpdatedAt ?? b.round.createdAt ?? '') || 0;
      return bTime - aTime;
    });
  }, [roundsByTourId, showArchivedSingleRounds, t, tours]);

  const formatRoundStatus = useCallback(
    (round: TourRoundSummary) => {
      if (!round.status) return null;
      const normalized = round.status.toLowerCase();
      if (normalized === 'active' || normalized === 'finished') {
        const timestamp = round.statusUpdatedAt ?? round.createdAt;
        if (!timestamp) {
          return t('tournaments.roundStatus', { status: round.status });
        }
        const dateLabel = new Date(timestamp).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
        return t('tournaments.roundStatusWithDate', { status: round.status, date: dateLabel });
      }
      return t('tournaments.roundStatus', { status: round.status });
    },
    [t]
  );

  const fetchRoundsForTour = useCallback(
    async (tourId: string) => {
      if (!user?.token) return;
      setRoundsByTourId((prev) => ({
        ...prev,
        [tourId]: {
          loading: true,
          error: null,
          rounds: prev[tourId]?.rounds ?? [],
        },
      }));
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=draft,open,active,playing,started,finished`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load rounds (${response.status})`);
        }
        const data = (await response.json()) as TourRoundSummary[];
        setRoundsByTourId((prev) => ({
          ...prev,
          [tourId]: {
            loading: false,
            error: null,
            rounds: data,
          },
        }));
      } catch (err) {
        setRoundsByTourId((prev) => ({
          ...prev,
          [tourId]: {
            loading: false,
            error: t('tournaments.roundsLoadError'),
            rounds: [],
          },
        }));
      }
    },
    [apiBaseUrl, t, user?.token]
  );

  const gameNameById = useMemo(
    () => new Map(gameDefinitions.map((game) => [game.id, getGameDisplayName(game, t('tournaments.gameFallbackName'))])),
    [gameDefinitions, t]
  );

  const fetchGameDefinitions = useCallback(async () => {
    if (!user?.token) return;
    try {
      const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to load game definitions (${response.status})`);
      }
      const data = (await response.json()) as GameDefinition[];
      setGameDefinitions(data ?? []);
    } catch {
      setGameDefinitions([]);
    }
  }, [apiBaseUrl, user?.token]);

  const fetchRoundGames = useCallback(
    async (roundId: string) => {
      if (!user?.token) return;
      setRoundGamesByRoundId((prev) => ({
        ...prev,
        [roundId]: {
          loading: true,
          error: null,
          gameIds: prev[roundId]?.gameIds ?? [],
        },
      }));
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load round (${response.status})`);
        }
        const data = (await response.json()) as {
          gameDefinitions?: string[];
          GameDefinitions?: string[];
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
        };
        const unique = Array.from(
          new Set(
            [
              ...(data.gameDefinitions ?? []),
              ...(data.GameDefinitions ?? []),
              ...(data.gameDefinitionIds ?? []),
              ...(data.GameDefinitionIds ?? []),
            ].filter(Boolean)
          )
        );
        setRoundGamesByRoundId((prev) => ({
          ...prev,
          [roundId]: {
            loading: false,
            error: null,
            gameIds: unique,
          },
        }));
      } catch (err) {
        setRoundGamesByRoundId((prev) => ({
          ...prev,
          [roundId]: {
            loading: false,
            error: t('tournaments.gamesLoadError'),
            gameIds: [],
          },
        }));
      }
    },
    [apiBaseUrl, t, user?.token]
  );

  const fetchTours = useCallback(async (playerIdOverride?: string) => {
    if (!user?.token) {
      clear();
      router.replace('/');
      return;
    }
    if (!user?.id && !playerIdOverride) return;
    setLoading(true);
    setError(null);
    try {
      let playerId = playerIdOverride ?? currentPlayerIdRef.current;
      if (!playerId && user?.id) {
        const playerResp = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (playerResp.status === 401 || playerResp.status === 403) {
          clear();
          router.replace('/');
          return;
        }
        if (playerResp.status === 404) {
          setTours([]);
          setStatusNote(t('tournaments.playerProvisioning'));
          return;
        }
        if (!playerResp.ok) throw new Error(await playerResp.text());
        const player = await (playerResp.json() as Promise<{ id?: string }>);
        playerId = player.id ?? null;
      }
      if (!playerId) {
        setTours([]);
        setStatusNote(t('tournaments.playerProvisioning'));
        return;
      }

      currentPlayerIdRef.current = playerId;
      const response = await fetch(`${apiBaseUrl}/api/tours?playerId=${playerId}&includeHidden=true`, {
        headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) throw new Error(await response.text());
      const data = (await response.json()) as Array<{
        id: string;
        tourName?: string;
        TourName?: string;
        status?: string;
        Status?: string;
        isHidden?: boolean;
        IsHidden?: boolean;
        playerNames?: string[];
        PlayerNames?: string[];
        createdAt?: string;
        CreatedAt?: string;
        imageKey?: string | null;
        ImageKey?: string | null;
        imageThumbKey?: string | null;
        ImageThumbKey?: string | null;
      }>;
      setTours(
        data.map((item) => ({
          id: item.id,
          tourName: item.tourName ?? item.TourName,
          status: item.status ?? item.Status,
          isHidden: item.isHidden ?? item.IsHidden ?? false,
          playerNames: item.playerNames ?? item.PlayerNames,
          createdAt: item.createdAt ?? item.CreatedAt,
          imageKey: item.imageKey ?? item.ImageKey ?? null,
          imageThumbKey: item.imageThumbKey ?? item.ImageThumbKey ?? null,
        }))
      );
      setStatusNote(data.length ? '' : t('tournaments.empty'));
      if (data.length === 0) {
        setCurrentTourId(null);
      }
    } catch {
      setError(t('tournaments.loadError'));
      setTours([]);
      setStatusNote('');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, user?.id, user?.token, clear, router, t]);

  useEffect(() => {
    if (isMobileLayout) {
      return;
    }

    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (isFocused) {
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    }
    const unsubscribe = socket.onMessage((message) => {
      const messageType = (message as { type?: string }).type;
      if (messageType === 'tour.initiate.rejected') {
        const payload = message as TourInitiateRejectedEvent;
        if (!pendingRequestIdRef.current || payload.requestId !== pendingRequestIdRef.current) {
          return;
        }
        pendingRequestIdRef.current = null;
        setTourStarting(false);
        const translated = payload.messageKey ? t(payload.messageKey) : '';
        const localized =
          translated && payload.messageKey && translated !== payload.messageKey
            ? translated
            : payload.message || t('tournaments.createFailed');
        setError(localized);
        setStatusNote(localized);
        return;
      }
      if (messageType === 'tour.update.ack') {
        pendingStatusRequestIdRef.current = null;
        setStatusNote(t('tournaments.statusUpdateAck'));
        return;
      }
      if (messageType === 'tour.created') {
        const payload = message as TourCreatedEvent;
        const incomingRequestId = payload.requestId ?? payload.RequestId;
        const isMatch = pendingRequestIdRef.current && incomingRequestId === pendingRequestIdRef.current;
        if (!isMatch) {
          fetchTours();
          setStatusNote(t('tournaments.createdNotice'));
          return;
        }
        pendingRequestIdRef.current = null;
        setTourStarting(false);
        const createdId = payload.tourId ?? payload.TourId ?? null;
        setCurrentTourId(createdId);
        setStatusNote(t('tournaments.createdWithId', { id: createdId ?? '' }));
        fetchTours();
        const debugSuffix = debug ? '?debug=true' : '';
        if (createdId) {
          router.push(`/tour/${createdId}/edit${debugSuffix}`);
        }
      }
    });
    const unsubscribeError = socket.onError(() => null);
    const unsubscribeConnection = socket.onConnectionChange((state) => {
      if (state !== 'connected') return;
      if (!pendingTourPayloadRef.current) return;
      const payload = pendingTourPayloadRef.current;
      pendingTourPayloadRef.current = null;
      const sent = socket.send(payload);
      if (sent) {
        setStatusNote(t('tournaments.creating'));
      } else {
        pendingTourPayloadRef.current = payload;
      }
    });
    return () => {
      unsubscribe();
      unsubscribeError();
      unsubscribeConnection();
    };
  }, [debug, fetchTours, isFocused, isMobileLayout, router, user?.token, t]);

  useEffect(() => {
    if (!tourStarting || !pendingRequestIdRef.current) return;
    const timer = setTimeout(() => {
      if (!pendingRequestIdRef.current) return;
      pendingRequestIdRef.current = null;
      setTourStarting(false);
      setStatusNote(t('tournaments.createFailed'));
    }, 12000);
    return () => clearTimeout(timer);
  }, [tourStarting, t]);

  useEffect(() => {
    if (user?.id) return;
    gameDefinitionsLoadedForUserIdRef.current = null;
  }, [user?.id]);

  useEffect(() => {
    if (isMobileLayout) return;
    if (!isNavigationReady) return;
    if (!user?.token) {
      clear();
      router.replace('/');
      return;
    }
    if (!user?.id) return;
    void fetchTours();
  }, [clear, fetchTours, isMobileLayout, isNavigationReady, router, user?.id, user?.token]);

  useEffect(() => {
    if (isMobileLayout) return;
    if (!isNavigationReady || !user?.id || !user?.token) return;
    if (gameDefinitionsLoadedForUserIdRef.current === user.id) return;
    gameDefinitionsLoadedForUserIdRef.current = user.id;
    void fetchGameDefinitions();
  }, [fetchGameDefinitions, isMobileLayout, isNavigationReady, user?.id, user?.token]);

  useEffect(() => {
    if (isMobileLayout) return;
    if (!user?.token || !isNavigationReady) return;
    tours.forEach((tour) => {
      fetchRoundsForTour(tour.id);
    });
  }, [fetchRoundsForTour, isMobileLayout, isNavigationReady, tours, user?.token]);

  useEffect(() => {
    if (isMobileLayout) return;
    if (!isNavigationReady || !user?.token) return;
    Object.values(roundsByTourId).forEach((state) => {
      state.rounds.forEach((round) => {
        if (!roundGamesByRoundId[round.id]) {
          fetchRoundGames(round.id);
        }
      });
    });
  }, [fetchRoundGames, isMobileLayout, isNavigationReady, roundGamesByRoundId, roundsByTourId, user?.token]);

  const handleStartTour = async () => {
    if (tourStarting) return;
    if (!user?.token || !user.id) {
      setError(t('tournaments.createFailed'));
      return;
    }
    setTourStarting(true);
    setError(null);
    setStatusNote(t('tournaments.creating'));

    let creatorPlayerId = currentPlayerIdRef.current;
    if (!creatorPlayerId) {
      const ensured = await ensureCurrentPlayer(apiBaseUrl, user.token, user.id, {
        timeoutMs: 10_000,
        intervalMs: 400,
      });
      if (ensured.status === 'resolved') {
        creatorPlayerId = ensured.playerId;
        currentPlayerIdRef.current = ensured.playerId;
      } else {
        setTourStarting(false);
        setStatusNote('');
        setError(
          ensured.status === 'unauthorized'
            ? t('tournaments.createFailed')
            : t('tournaments.creatorMemberRequired')
        );
        return;
      }
    }

    const socket = socketRef.current;
    socket.setAuthToken(user.token);
    const requestId = generateId();
    pendingRequestIdRef.current = requestId;
    const payload = {
      type: 'tour.initiate',
      requestId,
      tourName: t('tournaments.defaultName'),
      playerIds: [creatorPlayerId],
    };
    const sent = socket.send(payload);
    if (!sent) {
      pendingTourPayloadRef.current = payload;
      setStatusNote(t('tournaments.statusUpdateQueued'));
    }
  };

  const updateTourStatus = (tourId: string, status: string) => {
    if (!user?.token) return;
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (!['archived', 'sleep', 'cancelled'].includes(status)) return;
    const requestId = generateId();
    pendingStatusRequestIdRef.current = requestId;
    setStatusNote(t('tournaments.statusUpdateSent', { status }));
    const payload = {
      type: 'tour.update',
      tourId,
      status,
      requestId,
    };
    const sent = socket.send(payload);
    if (!sent) {
      setStatusNote(t('tournaments.statusUpdateQueued'));
    }
    logToBff('tournaments.status.selected', { tourId, status, requestId });
    if (status === 'archived') {
      console.info('[tournaments] archive', { tourId, status });
    }
    setOpenTourMenuId(null);
    setTours((prev) => {
      if (status === 'archived' || status === 'cancelled') {
        return prev.filter((t) => t.id !== tourId);
      }
      return prev.map((t) => (t.id === tourId ? { ...t, status } : t));
    });
  };

  const resolveInviteLink = async (tourId: string) => {
    return resolveTourInviteLink({
      apiBaseUrl,
      tourId,
      authToken: user?.token,
    });
  };

  const handleOpenShareModal = async (tour: TourSummary) => {
    setShareModalLoading(true);
    setShareModalError(null);
    setShareModalTourName(tour.tourName ?? t('tournaments.defaultName'));
    setShareModalLink(null);
    setOpenTourMenuId(null);

    try {
      const inviteLink = await resolveInviteLink(tour.id);
      setShareModalLink(inviteLink);
      setShareModalOpen(true);
    } catch (shareError) {
      setShareModalError(t('tournaments.shareLoadError'));
      setStatusNote(t('tournaments.shareLoadError'));
      logToBff('tournaments.share.link.error', {
        tourId: tour.id,
        message: shareError instanceof Error ? shareError.message : String(shareError),
      }, 'warn');
    } finally {
      setShareModalLoading(false);
    }
  };

  const handleShareInviteLink = async () => {
    if (!shareModalLink) {
      return;
    }

    const title = t('tournaments.shareTitle', { name: shareModalTourName ?? t('tournaments.defaultName') });
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        const navWithShare = navigator as {
          share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
          clipboard?: { writeText?: (value: string) => Promise<void> };
        };

        if (typeof navWithShare.share === 'function') {
          await navWithShare.share({
            title,
            url: shareModalLink,
          });
          return;
        }

        if (navWithShare.clipboard?.writeText) {
          await navWithShare.clipboard.writeText(shareModalLink);
          setStatusNote(t('tournaments.shareCopied'));
          return;
        }

        throw new Error('share unsupported');
      }

      await Share.share({
        title,
        message: shareModalLink,
      });
    } catch (shareError) {
      const message = shareError instanceof Error ? shareError.message : String(shareError);
      const canceled = message.toLowerCase().includes('cancel');
      if (!canceled) {
        setShareModalError(t('tournaments.shareSendError'));
      }
    }
  };

  const shareJoinCode = useMemo(() => {
    if (!shareModalLink) {
      return null;
    }

    const token = decodeURIComponent(shareModalLink.split('/').pop() ?? '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim()
      .toUpperCase();

    if (!token) {
      return null;
    }

    return token.length > 6 ? token.slice(0, 6) : token;
  }, [shareModalLink]);

  if (isMobileLayout) {
    return <MyToursScreen />;
  }

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;

  const openRoundFromTour = (tourId: string, round: TourRoundSummary) => {
    setCurrentTourId(tourId);
    setCurrentRoundId(round.id);
    const debugSuffix = debug ? '?debug=true' : '';
    const normalizedRoundStatus = (round.status ?? '').toLowerCase();
    const roundPath = normalizedRoundStatus === 'finished'
      ? `/round/${round.id}/summary${debugSuffix}`
      : normalizedRoundStatus === 'draft'
        ? `/round/${round.id}/roundstart${debugSuffix}`
        : `/round/${round.id}/hole/1/compact${debugSuffix}`;
    router.push(roundPath);
  };

  // Rendered at the top of the scrollable list (not the fixed header) so the
  // variable-height single-rounds content can scroll on web. See plan.
  const myToursListHeader = (
    <>
      {myToursAndRoundsSummary ? <Text style={styles.meta}>{myToursAndRoundsSummary}</Text> : null}
      <Pressable
        style={[styles.toggleButton, showArchivedSingleRounds && styles.toggleButtonActive]}
        onPress={() => setShowArchivedSingleRounds((prev) => !prev)}
        accessibilityRole="switch"
        accessibilityState={{ checked: showArchivedSingleRounds }}
      >
        <View style={styles.toggleIndicator}>
          <View style={[styles.toggleDot, showArchivedSingleRounds && styles.toggleDotActive]} />
        </View>
        <Text style={styles.toggleButtonText}>
          {showArchivedSingleRounds ? t('tournaments.hideFinishedSingleRounds') : t('tournaments.showFinishedSingleRounds')}
        </Text>
      </Pressable>
      {singleRoundRows.length > 0 ? (
        <View style={styles.roundsBlock}>
          <Text style={styles.roundsTitle}>
            {t('tournaments.hiddenToursSingleRoundsTitle')} ({singleRoundRows.length})
          </Text>
          <View style={styles.roundsGrid}>
            {singleRoundRows.map((row) => {
              const roundName = row.round.name ?? row.round.courseName ?? t('tournaments.roundFallbackName');
              const roundStatusLabel = formatRoundStatus(row.round);
              const normalizedRoundStatus = (row.round.status ?? '').toLowerCase();
              const isFinished = normalizedRoundStatus === 'finished';
              const isDraft = normalizedRoundStatus === 'draft';
              const roundActionLabel = isFinished
                ? t('tournaments.roundResults')
                : isDraft
                  ? t('tournaments.roundStart')
                  : t('tournaments.roundOpen');
              return (
                <View key={`${row.tourId}:${row.round.id}`} style={styles.roundRow}>
                  <View style={styles.roundCard}>
                    <View style={styles.roundHeaderRow}>
                      <View style={styles.roundHeader}>
                        <Text style={styles.roundName}>{roundName}</Text>
                        <Text style={styles.meta}>{row.tourName}</Text>
                        {roundStatusLabel ? <Text style={styles.roundMeta}>{roundStatusLabel}</Text> : null}
                      </View>
                      <View style={styles.hiddenRoundActions}>
                        <GoffyButton
                          label={roundActionLabel}
                          onPress={() => openRoundFromTour(row.tourId, row.round)}
                          size="compact"
                          tone="dark"
                          oldButtonStyle={styles.roundActionButton}
                          oldDisabledStyle={styles.primaryButtonDisabled}
                          style={styles.hiddenRoundActionButtonWrap}
                          textStyle={styles.roundActionButtonText}
                        />
                        <GoffyButton
                          label={t('tournaments.statusCancel')}
                          onPress={() => updateTourStatus(row.tourId, 'cancelled')}
                          size="compact"
                          tone="dark"
                          oldButtonStyle={styles.hiddenRoundCancelButton}
                          oldDisabledStyle={styles.primaryButtonDisabled}
                          style={styles.hiddenRoundActionButtonWrap}
                          textStyle={styles.hiddenRoundCancelButtonText}
                        />
                      </View>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      ) : null}
    </>
  );

  return (
    <View style={styles.shell}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>{t('welcome.myTours')}</Text>
          <Text style={styles.meta}>{statusNote}</Text>
        </View>
        <Pressable
          style={[styles.toggleButton, showArchived && styles.toggleButtonActive]}
          onPress={() => setShowArchived((prev) => !prev)}
          accessibilityRole="switch"
          accessibilityState={{ checked: showArchived }}
        >
          <View style={styles.toggleIndicator}>
            <View style={[styles.toggleDot, showArchived && styles.toggleDotActive]} />
          </View>
          <Text style={styles.toggleButtonText}>
            {showArchived ? t('tournaments.hideFinished') : t('tournaments.showFinished')}
          </Text>
        </Pressable>
      </View>
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <View style={styles.body}>
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          {myToursListHeader}
          {debug && user ? (
            <Text style={styles.debugUser}>User: {user.forename ?? user.id} ({user.id})</Text>
          ) : null}
          {loading ? <Text style={styles.meta}>{t('tournaments.loading')}</Text> : null}
          {visibleTours.map((tour) => {
            const normalizedStatus = (tour.status ?? '').toLowerCase();
            const statusLabel = normalizedStatus === 'active' ? null : tour.status;
            const tourTitle = tour.tourName ?? 'Tour';
          const playerLine = (tour.playerNames ?? []).map((name) => name.trim()).filter(Boolean).join(', ');
          const rawImage = tour.imageThumbKey ?? tour.imageKey ?? '';
          const imageUrl = rawImage && rawImage.startsWith('http') ? rawImage : rawImage ? `${apiBaseUrl}/api/tours/${tour.id}/image` : '';
          const imageSource = imageUrl ? { uri: imageUrl } : trophyImage;
          const menuOpen = openTourMenuId === tour.id;
            const handleNewRound = () => {
              setCurrentTourId(tour.id);
              const debugSuffix = debug ? '?debug=true' : '';
              router.push(`/tour/${tour.id}/round-init${debugSuffix}`);
            };
            const handleOpenTourEdit = () => {
              setCurrentTourId(tour.id);
              const debugSuffix = debug ? '?debug=true' : '';
              router.push(`/tour/${tour.id}/edit${debugSuffix}`);
            };
            const roundsState = roundsByTourId[tour.id];
            const rounds = roundsState?.rounds ?? [];
            const isTourClosed = normalizedStatus === 'finished' || normalizedStatus === 'cancelled';
            const activeRounds = rounds
              .filter((round) => {
                const status = (round.status ?? '').toLowerCase();
                if (status === 'cancelled') return false;
                if (status === 'finished') return !isTourClosed;
                return true;
              })
              .sort((a, b) => roundPlayDateOrder(a) - roundPlayDateOrder(b));
            const handleOpenRoundSettings = () => {
              const normalizedRounds = [...activeRounds].sort((a, b) => {
                const aTime = Date.parse(a.statusUpdatedAt ?? a.createdAt ?? '') || 0;
                const bTime = Date.parse(b.statusUpdatedAt ?? b.createdAt ?? '') || 0;
                return bTime - aTime;
              });
              const targetRound =
                normalizedRounds.find((round) => (round.status ?? '').toLowerCase() === 'draft') ??
                normalizedRounds.find((round) => {
                  const status = (round.status ?? '').toLowerCase();
                  return status === 'active' || status === 'playing';
                }) ??
                normalizedRounds[0];
              if (!targetRound) {
                setOpenTourMenuId(null);
                return;
              }
              setCurrentTourId(tour.id);
              setCurrentRoundId(targetRound.id);
              const queryParts: string[] = ['mode=update'];
              if (debug) {
                queryParts.push('debug=true');
              }
              router.push(`/round/${targetRound.id}/roundstart?${queryParts.join('&')}`);
              setOpenTourMenuId(null);
            };
            return (
              <View key={tour.id} style={[styles.card, menuOpen && styles.cardMenuOpen]}>
                <View style={styles.cardContent}>
                  <View style={styles.cardHeaderRow}>
                    <View style={styles.tourMenuAnchor}>
                      <Pressable
                        style={styles.tourMenuButton}
                        onPress={() => setOpenTourMenuId(menuOpen ? null : tour.id)}
                        accessibilityLabel={t('tournaments.menuLabel')}
                      >
                        <Text style={styles.tourMenuIcon}>⋯</Text>
                      </Pressable>
                      {menuOpen ? (
                        <View style={styles.tourMenu}>
                          <Pressable style={styles.tourMenuItem} onPress={handleOpenRoundSettings}>
                            <Text style={styles.tourMenuText}>{t('tournaments.roundSettings')}</Text>
                          </Pressable>
                          <Pressable style={styles.tourMenuItem} onPress={() => void handleOpenShareModal(tour)}>
                            <Text style={styles.tourMenuText}>{t('tournaments.shareCta')}</Text>
                          </Pressable>
                          <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'archived')}>
                            <Text style={styles.tourMenuText}>{t('tournaments.statusArchive')}</Text>
                          </Pressable>
                          <Pressable
                            style={styles.tourMenuItem}
                            onPress={() => {
                              console.info('[tournaments] sleep selected', { tourId: tour.id });
                              logToBff('tournaments.status.selected', {
                                tourId: tour.id,
                                status: 'sleep',
                                requestId: generateId(),
                              });
                              setOpenTourMenuId(null);
                            }}
                          >
                            <Text style={styles.tourMenuText}>{t('tournaments.statusSleep')}</Text>
                          </Pressable>
                          <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'cancelled')}>
                            <Text style={styles.tourMenuText}>{t('tournaments.statusCancel')}</Text>
                          </Pressable>
                        </View>
                      ) : null}
                    </View>
                    <Pressable style={styles.cardRow} onPress={handleOpenTourEdit} accessibilityLabel={t('tournaments.editCta')}>
                      <Image source={imageSource} style={styles.tourImage} resizeMode="cover" />
                      <View style={styles.cardText}>
                        <View style={styles.cardTitleRow}>
                          <Text style={styles.cardTitle}>{tourTitle}</Text>
                          {statusLabel ? <Text style={styles.cardStatus}>{statusLabel}</Text> : null}
                        </View>
                        {playerLine ? <Text style={styles.meta}>{playerLine}</Text> : null}
                        {debug ? <Text style={styles.meta}>ID: {tour.id}</Text> : null}
                        <View style={styles.tourActions}>
                          <Pressable
                            style={styles.editButton}
                            onPress={handleOpenTourEdit}
                            accessibilityLabel={t('tournaments.editCta')}
                          >
                            <Text style={styles.editButtonText}>{t('tournaments.editCta')}</Text>
                          </Pressable>
                        </View>
                      </View>
                    </Pressable>
                  </View>
                  <View style={styles.roundsColumn}>
                    <View style={styles.roundsHeaderRow}>
                      <Text style={styles.roundsTitle}>
                        {t('tournaments.roundsTitle')}
                        {roundsState ? ` (${activeRounds.length})` : ''}
                      </Text>
                    </View>
                    {roundsState?.loading ? <Text style={styles.roundsLoading}>{t('tournaments.roundsLoading')}</Text> : null}
                    {roundsState?.error ? <Text style={styles.roundsError}>{roundsState.error}</Text> : null}
                    {!roundsState?.loading && !roundsState?.error && activeRounds.length === 0 ? (
                      <Text style={styles.roundsEmpty}>{t('tournaments.roundsEmpty')}</Text>
                    ) : null}
                    <View style={styles.roundsGrid}>
                      {activeRounds.map((round) => {
                        const roundName = round.name ?? round.courseName ?? t('tournaments.roundFallbackName');
                        const roundStatusLabel = formatRoundStatus(round);
                        const normalizedRoundStatus = (round.status ?? '').toLowerCase();
                        const isFinished = normalizedRoundStatus === 'finished';
                        const isDraft = normalizedRoundStatus === 'draft';
                        const roundActionLabel = isFinished
                          ? t('tournaments.roundResults')
                          : isDraft
                            ? t('tournaments.roundStart')
                            : t('tournaments.roundOpen');
                        const debugSuffix = debug ? '?debug=true' : '';
                        const roundPath = isFinished
                          ? `/round/${round.id}/summary${debugSuffix}`
                          : normalizedRoundStatus === 'draft'
                            ? `/round/${round.id}/roundstart${debugSuffix}`
                            : `/round/${round.id}/hole/1/compact${debugSuffix}`;
                        const roundGamesState = roundGamesByRoundId[round.id];
                        const roundGameNames = (roundGamesState?.gameIds ?? [])
                          .map((id) => gameNameById.get(id) ?? t('tournaments.gameFallbackName'))
                          .filter(Boolean);
                        return (
                          <Pressable
                            key={round.id}
                            style={styles.roundRow}
                            onPress={() => {
                              setCurrentTourId(tour.id);
                              setCurrentRoundId(round.id);
                              router.push(roundPath);
                            }}
                            accessibilityLabel={roundName}
                          >
                            <View style={styles.roundCard}>
                              <View style={styles.roundHeaderRow}>
                                <View style={styles.roundHeader}>
                                  <Text style={styles.roundName}>{roundName}</Text>
                                  {roundStatusLabel ? <Text style={styles.roundMeta}>{roundStatusLabel}</Text> : null}
                                </View>
                                <GoffyButton
                                  label={roundActionLabel}
                                  onPress={() => {
                                    setCurrentTourId(tour.id);
                                    setCurrentRoundId(round.id);
                                    router.push(roundPath);
                                  }}
                                  size="compact"
                                  tone="dark"
                                  oldButtonStyle={styles.roundActionButton}
                                  oldDisabledStyle={styles.primaryButtonDisabled}
                                  style={styles.roundActionButtonWrap}
                                  textStyle={styles.roundActionButtonText}
                                />
                              </View>
                              <View style={styles.roundGamesSection}>
                                {roundGamesState?.loading ? (
                                  <Text style={styles.roundGamesMeta}>{t('tournaments.gamesLoading')}</Text>
                                ) : null}
                                {roundGamesState?.error ? (
                                  <Text style={styles.roundGamesError}>{roundGamesState.error}</Text>
                                ) : null}
                                {!roundGamesState?.loading && !roundGamesState?.error ? (
                                  <Text style={styles.roundGamesMeta}>
                                    {t('tournaments.gamesCount', { count: roundGameNames.length })}
                                  </Text>
                                ) : null}
                                <View style={styles.roundGamesList} />
                              </View>
                            </View>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                  <GoffyButton
                    label={t('tournaments.newRoundCta')}
                    onPress={handleNewRound}
                    tone="accent"
                    oldButtonStyle={styles.newRoundButton}
                    oldTextStyle={styles.newRoundButtonText}
                    oldDisabledStyle={styles.primaryButtonDisabled}
                    style={styles.newRoundButtonWide}
                  />
                </View>
              </View>
            );
          })}
          {!visibleTours.length && !loading && !error ? <Text style={styles.subtitle}>{t('tournaments.noTours')}</Text> : null}
        </ScrollView>
        <View style={styles.sidePanel}>
          <Image source={trophyImage} style={styles.heroImage} resizeMode="contain" />
          <View>
            <GoffyButton
              label={tourStarting ? t('tournaments.creating') : t('tournaments.createCta')}
              onPress={handleStartTour}
              disabled={tourStarting}
              size="compact"
              tone="accent"
              oldButtonStyle={styles.primaryButton}
              oldTextStyle={styles.primaryText}
              oldDisabledStyle={styles.primaryButtonDisabled}
              style={styles.primaryButtonImageMode}
            />
          </View>
        </View>
      </View>
      <Modal
        transparent
        visible={shareModalOpen}
        animationType="fade"
        onRequestClose={() => setShareModalOpen(false)}
      >
        <Pressable style={styles.shareOverlay} onPress={() => setShareModalOpen(false)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>
              {t('tournaments.shareTitle', { name: shareModalTourName ?? t('tournaments.defaultName') })}
            </Text>
            <View style={styles.shareCodeCard}>
              <Text style={styles.shareCodeLabel}>{t('players.joinWithCode')}</Text>
              <Text style={styles.shareCodeValue}>{shareJoinCode || '--- ---'}</Text>
              {shareModalLoading ? (
                <ActivityIndicator size="small" color={appColors.accent.primary} />
              ) : null}
              {shareModalLink ? (
                <View style={styles.shareQrWrapper}>
                  <QRCode value={shareModalLink} size={220} color="#000000" backgroundColor="#ffffff" />
                </View>
              ) : null}
            </View>
            {shareModalError ? <Text style={styles.error}>{shareModalError}</Text> : null}
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('tournaments.shareAction')}
                onPress={() => void handleShareInviteLink()}
                disabled={!shareModalLink || shareModalLoading}
                tone="accent"
                size="compact"
                style={styles.shareActionButton}
              />
              <GoffyButton
                label={t('tournaments.shareClose')}
                onPress={() => setShareModalOpen(false)}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: appColors.background.app,
    padding: 28,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 20,
  },
  headerText: {
    gap: 6,
  },
  title: {
    fontSize: 32,
    fontWeight: '800',
    color: appColors.text.heading,
  },
  meta: {
    color: appColors.accent.primary,
  },
  provisioningRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  provisioningRetryRow: {
    gap: 8,
  },
  retryButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  retryButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '700',
  },
  error: {
    color: '#ff9fa6',
    fontWeight: '700',
  },
  body: {
    flex: 1,
    flexDirection: 'row',
    gap: 24,
  },
  list: {
    flex: 1,
  },
  listContent: {
    gap: 16,
    paddingBottom: 40,
  },
  sidePanel: {
    width: 320,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 12,
    alignSelf: 'flex-start',
  },
  heroImage: {
    width: 120,
    height: 120,
    alignSelf: 'center',
  },
  subtitle: {
    color: appColors.text.secondary,
  },
  smallNote: {
    color: appColors.text.secondary,
  },
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 8,
    flexDirection: 'column',
    alignItems: 'stretch',
    position: 'relative',
    zIndex: 1,
  },
  cardMenuOpen: {
    borderColor: '#A1D493',
    zIndex: 10,
    elevation: 10,
  },
  cardContent: {
    width: '100%',
    gap: 12,
  },
  cardHeaderRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  cardColumns: {
    flexDirection: 'column',
    gap: 4,
    alignItems: 'stretch',
  },
  cardRow: {
    flexDirection: 'row',
    gap: 12,
    flex: 1,
    alignItems: 'flex-start',
  },
  cardText: {
    flex: 1,
    gap: 6,
  },
  tourImage: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  tourImagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  tourInfo: {
    alignSelf: 'stretch',
    width: '100%',
  },
  tourMenuAnchor: {
    position: 'relative',
    marginRight: 12,
    zIndex: 20,
  },
  tourMenuButton: {
    width: 36,
    height: 36,
    borderRadius: 12,
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tourMenuIcon: {
    color: appColors.text.secondary,
    fontSize: 18,
    fontWeight: '600',
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.heading,
  },
  roundsColumn: {
    width: '100%',
    gap: 10,
  },
  roundsHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  roundsTitle: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  roundsLoading: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  roundsEmpty: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  roundsError: {
    color: '#ff9fa6',
    fontSize: 12,
    fontWeight: '600',
  },
  roundRow: {
    width: '100%',
    alignSelf: 'stretch',
  },
  roundCard: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 8,
  },
  roundHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  roundHeader: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  roundName: {
    color: '#C8C8B0',
    fontSize: 14,
    fontWeight: '700',
  },
  roundMeta: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  roundActionButtonWrap: {
    alignSelf: 'center',
    marginLeft: 'auto',
  },
  roundActionButton: {
    paddingHorizontal: 10,
    height: 36,
  },
  roundActionButtonText: {
    color: appColors.text.secondary,
    fontSize: 14,
    fontWeight: '700',
  },
  hiddenRoundActions: {
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-end',
    marginLeft: 'auto',
  },
  hiddenRoundActionButtonWrap: {
    width: 70,
  },
  hiddenRoundCancelButton: {
    paddingHorizontal: 10,
    height: 36,
    borderColor: '#8c2f2f',
  },
  hiddenRoundCancelButtonText: {
    color: '#ffb3b3',
    fontSize: 14,
    fontWeight: '700',
  },
  roundGamesSection: {
    gap: 6,
  },
  roundGamesTitle: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  roundGamesMeta: {
    color: '#A1D493',
    fontSize: 12,
  },
  roundGamesError: {
    color: '#ff9fa6',
    fontSize: 12,
    fontWeight: '600',
  },
  roundGamesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  roundGameTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(52,211,153,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
  },
  roundGameText: {
    color: appColors.text.primary,
    fontSize: 11,
    fontWeight: '700',
  },
  roundsGrid: {
    gap: 8,
  },
  cardStatus: {
    fontSize: 12,
    fontWeight: '700',
    color: '#A1D493',
    backgroundColor: 'rgba(45,98,53,0.24)',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 2,
  },
  newRoundButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  newRoundButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  newRoundButtonWide: {
    alignSelf: 'stretch',
    width: '100%',
  },
  tourActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 6,
  },
  editButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.4)',
    backgroundColor: 'rgba(47,138,78,0.12)',
  },
  editButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  tourMenu: {
    position: 'absolute',
    left: 0,
    top: 44,
    backgroundColor: appColors.background.panel,
    borderRadius: 10,
    padding: 10,
    gap: 6,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.32)',
    minWidth: 140,
    zIndex: 25,
  },
  tourMenuItem: {
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tourMenuText: {
    color: appColors.text.secondary,
    fontWeight: '600',
  },
  debugUser: {
    color: '#A1D493',
    fontSize: 12,
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panelAlt,
  },
  toggleButtonActive: {
    backgroundColor: 'rgba(45,98,53,0.35)',
  },
  toggleButtonText: {
    color: appColors.text.primary,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  toggleIndicator: {
    width: 30,
    height: 18,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(124,171,134,0.65)',
    padding: 2,
    justifyContent: 'center',
  },
  toggleDot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    backgroundColor: '#8fc49b',
  },
  toggleDotActive: {
    alignSelf: 'flex-end',
    backgroundColor: '#A1D493',
  },
  tourInput: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.app,
    color: appColors.text.heading,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  membersCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    padding: 10,
    gap: 8,
  },
  membersTitle: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  membersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  memberChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  memberChipSelected: {
    borderColor: '#A1D493',
    backgroundColor: 'rgba(52,211,153,0.18)',
  },
  memberChipLocked: {
    borderColor: '#8ecf9a',
    backgroundColor: 'rgba(42,119,66,0.18)',
  },
  memberChipText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  memberChipTextSelected: {
    color: '#323835',
  },
  memberChipTextLocked: {
    color: '#2d6b66',
  },
  temporaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  temporaryInput: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 220,
    minWidth: 0,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    color: appColors.text.primary,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  secondaryButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#C8C8B0',
    paddingHorizontal: 10,
    paddingVertical: 8,
    maxWidth: '100%',
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  secondaryButtonImageMode: {
    width: 124,
    height: 34,
  },
  secondaryButtonImageText: {
    fontSize: 14,
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  shareCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#07200D',
    padding: 18,
    gap: 12,
  },
  shareTitle: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  shareCodeCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#04210D',
    padding: 16,
    gap: 10,
    alignItems: 'center',
  },
  shareCodeLabel: {
    color: '#8FA08B',
    fontSize: 12,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  shareCodeValue: {
    color: '#AEE79A',
    fontSize: 48,
    letterSpacing: 6,
    fontWeight: '700',
    textAlign: 'center',
  },
  shareQrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: '#f6f8f6',
    padding: 10,
  },
  shareLinkText: {
    color: appColors.text.primary,
    fontSize: 12,
    textAlign: 'center',
  },
  shareActions: {
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'flex-start',
  },
  shareActionButton: {
    width: '100%',
  },
  primaryButton: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonImageMode: {
    width: 124,
    height: 34,
  },
  shareButtonImageMode: {
    width: 180,
    height: 56,
  },
  primaryText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 16,
  },
});

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
